import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

import { ensureEntity } from '@/lib/crm/object-cards/entityFetchBatcher';
import {
  getActivePreview,
  mergeProjection,
  useActivePreviewHandle,
} from '@/lib/crm/preview';
import { hasCompleteObject, subscribeToObject } from '@/lib/crm/store';
import type { CrmObject } from '@/types/crm';

const NOOP_SUBSCRIBE = (_cb: () => void) => () => {};

/**
 * Shared body for {@link useCrmObjectSnapshot} and {@link useCrmObjectLive}.
 *
 * Runs one store subscription and one overlay subscription either way — every
 * hook below is called unconditionally, so `projected` only decides which of
 * the two already-computed views is returned. Subscriptions are shared at the
 * store level, so a component reading BOTH views (the two sides of a diff)
 * costs no extra fetch.
 */
function useCrmObjectView(
  entityId: string | undefined,
  orgId: number | undefined,
  projected: boolean
): CrmObject | undefined {
  const sub = useMemo(
    () => (entityId ? subscribeToObject(entityId) : null),
    [entityId]
  );

  const subscribe = useCallback(
    (cb: () => void) => (sub ? sub.subscribe(cb) : NOOP_SUBSCRIBE(cb)),
    [sub]
  );
  const getSnapshot = useCallback(
    () => (sub ? sub.getSnapshot() : undefined),
    [sub]
  );

  const object = useSyncExternalStore(subscribe, getSnapshot);

  // Context inside a provider subtree; the ACTIVE registered provider
  // otherwise — so components rendered outside the provider (e.g. inside the
  // out-of-subtree record slideout) still resolve projections while a
  // preview is active. Null when no provider is mounted anywhere.
  const preview = useActivePreviewHandle();
  const overlaySubscribe = useCallback(
    (cb: () => void) =>
      preview && entityId
        ? preview.subscribeToOverlay(entityId, cb)
        : NOOP_SUBSCRIBE(cb),
    [preview, entityId]
  );
  const overlayGetSnapshot = useCallback(
    () => (preview && entityId ? preview.getOverlay(entityId) : undefined),
    [preview, entityId]
  );
  const overlay = useSyncExternalStore(overlaySubscribe, overlayGetSnapshot);

  const merged = useMemo(
    () => (overlay ? mergeProjection(object, overlay, orgId ?? 0) : object),
    [object, overlay, orgId]
  );

  // Derive a boolean fetch trigger from the subscription result. When
  // clearAll() empties the store (e.g., on org switch), `object` transitions
  // from CrmObject → undefined, flipping `needsFetch` from false → true.
  // This causes the effect below to re-run and call ensureEntity(),
  // re-fetching the entity.
  //
  // Without this, the effect deps [orgId, entityId] don't change after a
  // store clear, so the effect never re-fires and entity badges stay in
  // loading state forever. Using a boolean (not the object reference itself)
  // avoids unnecessary effect runs on WS property updates where the object
  // reference changes but needsFetch stays false.
  const needsFetch = entityId ? !object : false;

  const suppressFetch = overlay?.kind === 'create';

  useEffect(() => {
    if (
      entityId &&
      orgId &&
      orgId > 0 &&
      !hasCompleteObject(entityId) &&
      !suppressFetch
    ) {
      // Same-commit guard: when this hook mounts in the SAME commit as the
      // provider (e.g. a deep-linked slideout rendering beside the proposal
      // panel on first load), the render-time overlay read predates the
      // provider's layout-effect registration — `suppressFetch` in this
      // closure is stale. Re-check the live handle at effect time so a
      // create-overlay id never fires a doomed fetch.
      const liveOverlayKind = (preview ?? getActivePreview())?.getOverlay(
        entityId
      )?.kind;
      if (liveOverlayKind === 'create') {
        return;
      }
      ensureEntity(orgId, entityId);
    }
  }, [orgId, entityId, needsFetch, suppressFetch, preview]);

  return projected ? merged : object;
}

/**
 * The record as a proposal would leave it — the live store record with any
 * active preview projection applied on top. This is the RIGHT-hand side of a
 * proposal diff, and the correct read for any surface showing "what this
 * becomes if approved".
 *
 * @param entityId - The entity UUID to subscribe to. If undefined, returns undefined.
 * @param orgId - Organization ID for triggering fetch. If undefined or <= 0, skips fetch.
 * @returns CrmObject if loaded, undefined if not yet loaded or if entityId is undefined
 *
 * @example
 * // The stage this deal MOVES TO under the proposal being previewed:
 * const projected = useCrmObjectSnapshot(dealId, orgId);
 * const proposedStageId = projected?.associations?.find(
 *   a => a.association_type_name === 'ahoy_deal_to_deal_pipeline_stage'
 * )?.target_entity_id;
 */
export function useCrmObjectSnapshot(
  entityId: string | undefined,
  orgId: number | undefined
): CrmObject | undefined {
  return useCrmObjectView(entityId, orgId, true);
}

/**
 * The record as it is RIGHT NOW — the live store record with no proposal
 * projected onto it. This is the LEFT-hand side of a proposal diff.
 *
 * Reach for this whenever you are rendering "current vs proposed". Reading the
 * projected view for the current side is the bug that makes both columns show
 * the same value: inside a `CrmPreviewProvider` the proposal has already been
 * merged into the projected record, so "current" silently becomes "proposed".
 * Deriving the left side from a stored snapshot (a payload field, a backend
 * `current_values` blob) is not the fix — it goes stale and diverges from the
 * store. Read the live record here and the projected record on the right.
 *
 * @param entityId - The entity UUID to subscribe to. If undefined, returns undefined.
 * @param orgId - Organization ID for triggering fetch. If undefined or <= 0, skips fetch.
 * @returns CrmObject if loaded, undefined if not yet loaded or if entityId is undefined
 *
 * @example
 * // The stage this deal is in today, regardless of what the proposal proposes:
 * const live = useCrmObjectLive(dealId, orgId);
 * const currentStageId = live?.associations?.find(
 *   a => a.association_type_name === 'ahoy_deal_to_deal_pipeline_stage'
 * )?.target_entity_id;
 */
export function useCrmObjectLive(
  entityId: string | undefined,
  orgId: number | undefined
): CrmObject | undefined {
  return useCrmObjectView(entityId, orgId, false);
}
